import React, { useMemo } from 'react';
import { Box, Grid, Alert, CircularProgress, useTheme } from '@mui/material';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WarningIcon from '@mui/icons-material/Warning';
import BalanceIcon from '@mui/icons-material/Balance';
import { StatCard, DonutChart } from '../../../../../shared/components/charts';
import { DashboardCard, CohortGuard } from '../../../../../shared/components';
import { useMetricsStats } from '../../../../../shared/hooks';
import type { MetricsFilters } from '../../../../../shared/api/types';
import { useOverallStressStats, useOverallStressDistribution } from '../hooks';

interface StressStatsColumnProps {
  filterParams: MetricsFilters;
  isCentralAdmin: boolean;
  isAnonymised: boolean;
  stacked?: boolean;
}

/**
 * Renders Overall Physiological Stress's four stat cards + the Stress Level
 * Distribution donut for a single filter scope. Safe to render once per group.
 */
export const StressStatsColumn: React.FC<StressStatsColumnProps> = ({
  filterParams,
  isCentralAdmin,
  isAnonymised,
  stacked = false,
}) => {
  const theme = useTheme();
  const statsQuery = useOverallStressStats({
    ...filterParams,
    metrics: ['stress_score', 'high_stress_days', 'hrv', 'stress_recovery_balance'],
  });
  const distributionQuery = useOverallStressDistribution(filterParams);
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      { metric: stats.stress_score, icon: SelfImprovementIcon },
      { metric: stats.hrv, icon: MonitorHeartIcon },
      { metric: stats.high_stress_days, icon: WarningIcon },
      { metric: stats.stress_recovery_balance, icon: BalanceIcon },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const distributionData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      label: item.label,
      value: item.value,
      color:
        item.name === 'high' || item.label.toLowerCase().includes('high')
          ? theme.palette.warning.main
          : item.name === 'medium' || item.label.toLowerCase().includes('medium')
            ? theme.palette.success.main
            : theme.palette.info.main,
    }));
  }, [distributionQuery.data, theme]);

  const isLoading = statsQuery.isLoading || distributionQuery.isLoading;
  const statColSize = stacked ? { xs: 12, sm: 6 } : { xs: 12, sm: 6, md: 3 };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {isLoading
          ? [1, 2, 3, 4].map((i) => (
              <Grid size={statColSize} key={i}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              </Grid>
            ))
          : statCards.map((stat, index) => (
              <Grid size={statColSize} key={index}>
                <StatCard metric={stat.metric} icon={stat.icon} />
              </Grid>
            ))}
      </Grid>

      <DashboardCard
        title='Stress Level Distribution'
        subtitle='Proportion of employees within high, medium, and low stress zones.'
        metricKey='stress_score'
        expandable={false}
      >
        <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={statsQuery.isLoading}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
              <CircularProgress />
            </Box>
          ) : distributionData.length > 0 ? (
            <DonutChart data={distributionData} height={260} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
              <Alert severity='info'>No distribution data available</Alert>
            </Box>
          )}
        </CohortGuard>
      </DashboardCard>
    </Box>
  );
};
